import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../../../components/Navbar';

export default function ChorleywoodRewiring() {
  useEffect(() => {
    document.title = 'Chorleywood Rewiring Services | NICEIC Approved Electricians | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional rewiring services in Chorleywood. NICEIC approved electricians for full house rewiring, partial rewiring, and consumer unit upgrades. Call 020 3198 1818 for a free quote.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'rewiring Chorleywood, house rewiring Chorleywood, electrical rewiring, NICEIC electrician Chorleywood, consumer unit upgrade');
    }
    
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/services/installations/rewiring/chorleywood`);
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "@id": `${import.meta.env.VITE_SITE_URL}/services/installations/rewiring/chorleywood#localbusiness`,
          "name": "Beyond Electrical - Chorleywood Rewiring Services",
          "image": `${import.meta.env.VITE_SITE_URL}/logo.png`,
          "telephone": "020 3198 1818",
          "priceRange": "££",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Chorleywood",
            "addressRegion": "Hertfordshire",
            "addressCountry": "GB"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "51.6542",
            "longitude": "-0.5183"
          },
          "url": `${import.meta.env.VITE_SITE_URL}/services/installations/rewiring/chorleywood`,
          "areaServed": {
            "@type": "City",
            "name": "Chorleywood"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "08:00",
              "closes": "18:00"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": `${import.meta.env.VITE_SITE_URL}/services/installations/rewiring/chorleywood#service`,
          "serviceType": "Electrical Rewiring Services",
          "provider": {
            "@id": `${import.meta.env.VITE_SITE_URL}/services/installations/rewiring/chorleywood#localbusiness`
          },
          "areaServed": {
            "@type": "City",
            "name": "Chorleywood"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Rewiring Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Full House Rewiring"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Partial Rewiring"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Consumer Unit Upgrades"
                }
              }
            ]
          }
        }
      ]
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center text-white overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/7fd2ea615dfb5898f6ff844c2c0f93ce.jpg"
            alt="Professional Rewiring Services in Chorleywood"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/80 to-[#0F2847]/70"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block px-4 py-2 bg-[#FFD700] text-[#0A1628] rounded-full text-sm font-semibold mb-4">
            Chorleywood Rewiring Specialists
          </span>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Professional Rewiring Services in Chorleywood
          </h1>
          <p className="text-xl mb-8 text-gray-200">
            Expert electrical rewiring for homes and businesses • NICEIC Approved • 020 3198 1818
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="tel:02031981818" className="px-8 py-4 bg-[#FFD700] text-[#0A1628] rounded-lg font-semibold hover:bg-[#FFD700]/90 transition-all whitespace-nowrap">
              Call 020 3198 1818
            </a>
            <a href="/contact" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-[#FFD700] rounded-lg font-semibold hover:bg-white/20 transition-all whitespace-nowrap">
              Get Free Quote
            </a>
          </div>
        </div>
      </section>
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">Our Rewiring Services in Chorleywood</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">From complete house rewires to targeted upgrades, we provide comprehensive electrical rewiring solutions for homes across Chorleywood</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: 'ri-home-gear-line', title: 'Full House Rewiring', desc: 'Complete electrical system replacement for older properties' },
              { icon: 'ri-tools-line', title: 'Partial Rewiring', desc: 'Targeted rewiring for specific rooms or circuits' },
              { icon: 'ri-dashboard-line', title: 'Consumer Unit Upgrades', desc: 'Modern fuse box installation with RCD protection' },
              { icon: 'ri-flashlight-line', title: 'Circuit Extensions', desc: 'Adding new circuits for additional power needs' },
              { icon: 'ri-shield-check-line', title: 'Safety Inspections', desc: 'EICR testing and electrical safety certificates' },
              { icon: 'ri-timer-line', title: 'Minimal Disruption', desc: 'Efficient work schedules to suit your lifestyle' }
            ].map((service, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-all">
                <div className="w-16 h-16 bg-amber-500 rounded-lg flex items-center justify-center mb-6"><i className={`${service.icon} text-3xl text-black`}></i></div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">Signs Your Chorleywood Home Needs Rewiring</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">Don&apos;t ignore these warning signs - outdated wiring can be dangerous</p>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {['Property over 30 years old with original wiring','Frequent circuit breaker trips or blown fuses','Flickering or dimming lights throughout the home','Burning smell or scorch marks around outlets','Outlets or switches that feel warm to touch','Insufficient sockets leading to overuse of extensions','Old-style fuse boxes or lack of RCD protection','Planning major renovations or extensions'].map((sign, index) => (
              <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <i className="ri-alert-line text-2xl text-amber-500 flex-shrink-0 mt-1"></i>
                <p className="text-gray-700">{sign}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">Benefits of Professional Rewiring</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: 'ri-shield-check-line', title: 'Enhanced Safety', desc: 'Eliminate fire risks from outdated or damaged wiring' },
              { icon: 'ri-funds-line', title: 'Increased Property Value', desc: 'Modern electrical systems add value to your home' },
              { icon: 'ri-flashlight-line', title: 'Improved Performance', desc: 'Reliable power supply for modern appliances' },
              { icon: 'ri-money-pound-circle-line', title: 'Energy Efficiency', desc: 'Reduce energy waste and lower electricity bills' },
              { icon: 'ri-file-shield-line', title: 'Insurance Compliance', desc: 'Meet insurance requirements for electrical safety' },
              { icon: 'ri-home-smile-line', title: 'Peace of Mind', desc: 'Certified safe electrical installation throughout' }
            ].map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-sm text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6"><i className={`${benefit.icon} text-3xl text-black`}></i></div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">
              Why Choose Beyond Electrical for Rewiring in Chorleywood?
            </h2>
            <p className="text-xl text-gray-600">
              Trusted local electricians with proven expertise
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'NICEIC Approved',
                description: 'Fully certified and insured electrical contractors'
              },
              {
                icon: 'ri-time-line',
                title: 'Fast Response',
                description: 'Quick quotes and flexible scheduling to suit you'
              },
              {
                icon: 'ri-hand-coin-line',
                title: 'Competitive Pricing',
                description: 'Transparent quotes with no hidden costs'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Local Service',
                description: 'Based in your area, we know Chorleywood well'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#FFD700] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${item.icon} text-3xl text-[#0A1628]`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Rewiring Services Across Chorleywood</h2>
          <p className="text-gray-600 mb-8">We provide professional rewiring services throughout Chorleywood and surrounding areas</p>
          <div className="flex flex-wrap justify-center gap-3">
            {['Chorleywood Village','Chorleywood Bottom','Heronsgate','Chenies','Little Chalfont','Rickmansworth','Sarratt'].map((area, index) => (
              <span key={index} className="px-6 py-2 bg-amber-500 text-black rounded-full font-semibold whitespace-nowrap">{area}</span>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-br from-[#0A1628] to-[#0F2847] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Upgrade Your Electrical System?
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Contact Beyond Electrical today for a free, no-obligation quote for rewiring services in Chorleywood
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="tel:02031981818" className="px-8 py-4 bg-[#FFD700] text-[#0A1628] rounded-lg font-semibold hover:bg-[#FFD700]/90 transition-all whitespace-nowrap">
              <i className="ri-phone-line mr-2"></i>
              Call 020 3198 1818
            </a>
            <a href="/contact" className="px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-[#FFD700] rounded-lg font-semibold hover:bg-white/20 transition-all whitespace-nowrap">
              Request Free Quote
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
